import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Loader2, Search, Sparkles, CheckCircle, AlertCircle } from "lucide-react";

interface BookResult {
  title: string;
  position: number;
  isbn13?: string;
  isbn10?: string;
  coverUrl?: string;
  bookId?: string;
  isFound: boolean;
}

interface SeriesSuggestion {
  seriesName: string;
  authorName: string;
  source: string;
  books: BookResult[];
  enrichmentStats?: {
    total: number;
    found: number;
  };
}

interface AISuggestSeriesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSeriesSelected?: (seriesData: SeriesSuggestion) => void;
}

export function AISuggestSeriesDialog({
  open,
  onOpenChange,
  onSeriesSelected,
}: AISuggestSeriesDialogProps) {
  const [seriesName, setSeriesName] = useState("");
  const [authorName, setAuthorName] = useState("");
  const [searching, setSearching] = useState(false);
  const [suggestions, setSuggestions] = useState<SeriesSuggestion[]>([]);

  const handleSearch = async () => {
    if (!seriesName.trim()) {
      toast.error("Please enter a series name");
      return;
    }

    setSearching(true);
    try {
      const { data, error } = await supabase.functions.invoke('suggest-series-books', {
        body: {
          seriesName,
          authorName: authorName || undefined,
        },
      });

      if (error) throw error;

      if (data?.suggestions && data.suggestions.length > 0) {
        setSuggestions(data.suggestions);
        const firstSuggestion = data.suggestions[0];
        if (firstSuggestion.enrichmentStats) {
          toast.success(
            `Found ${firstSuggestion.enrichmentStats.found} of ${firstSuggestion.enrichmentStats.total} books in your database`
          );
        }
      } else {
        toast.info("No series found. Try adjusting the name or author.");
        setSuggestions([]);
      }
    } catch (error: any) {
      console.error('AI search error:', error);
      toast.error(`Search failed: ${error.message}`);
      setSuggestions([]);
    } finally {
      setSearching(false);
    }
  };

  const handleSelectSeries = (series: SeriesSuggestion) => {
    onSeriesSelected?.(series);
    onOpenChange(false);
    toast.success("Series suggestion created! You can now edit and approve it.");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            AI Series Suggestion
          </DialogTitle>
          <DialogDescription>
            Enter a series name and let AI find all the books in the series
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="seriesName">Series Name</Label>
              <Input
                id="seriesName"
                placeholder="e.g., Harry Potter"
                value={seriesName}
                onChange={(e) => setSeriesName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="authorName">Author (Optional)</Label>
              <Input
                id="authorName"
                placeholder="e.g., J.K. Rowling"
                value={authorName}
                onChange={(e) => setAuthorName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              />
            </div>
          </div>

          <Button
            onClick={handleSearch}
            disabled={searching || !seriesName.trim()}
            className="w-full"
          >
            {searching ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                AI is searching & auto-enriching...
              </>
            ) : (
              <>
                <Search className="mr-2 h-4 w-4" />
                Find Series with AI
              </>
            )}
          </Button>

          {suggestions.length > 0 && (
            <ScrollArea className="h-[400px] pr-4">
              <div className="space-y-3">
                <h3 className="text-sm font-semibold">AI Found These Series:</h3>
                {suggestions.map((series, idx) => (
                  <Card key={idx} className="p-4 hover:border-primary transition-colors">
                    <div className="space-y-3">
                      <div className="flex items-start justify-between">
                        <div>
                          <h4 className="font-semibold text-lg">{series.seriesName}</h4>
                          <p className="text-sm text-muted-foreground">by {series.authorName}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          {series.enrichmentStats && (
                            <Badge 
                              variant={series.enrichmentStats.found === series.enrichmentStats.total ? "default" : "secondary"}
                              className="flex items-center gap-1"
                            >
                              {series.enrichmentStats.found === series.enrichmentStats.total ? (
                                <CheckCircle className="h-3 w-3" />
                              ) : (
                                <AlertCircle className="h-3 w-3" />
                              )}
                              {series.enrichmentStats.found}/{series.enrichmentStats.total} in DB
                            </Badge>
                          )}
                          <Badge variant="outline">
                            {series.books?.length || 0} books
                          </Badge>
                        </div>
                      </div>

                      {series.books && series.books.length > 0 && (
                        <div className="flex gap-2 overflow-x-auto pb-2">
                          {series.books.slice(0, 7).map((book, bookIdx) => (
                            <div key={bookIdx} className="flex flex-col items-center min-w-[80px]">
                              <div className="relative">
                                {book.isFound && book.coverUrl ? (
                                  <img
                                    src={book.coverUrl}
                                    alt={book.title}
                                    className="w-[80px] h-[120px] object-cover rounded shadow-md"
                                  />
                                ) : (
                                  <div className={`w-[80px] h-[120px] rounded flex items-center justify-center text-xs text-center p-2 ${
                                    book.isFound 
                                      ? 'bg-primary/10 border-2 border-primary/30' 
                                      : 'bg-muted border-2 border-dashed border-muted-foreground/30'
                                  }`}>
                                    {book.isFound ? (
                                      <CheckCircle className="h-6 w-6 text-primary" />
                                    ) : (
                                      <span className="text-muted-foreground">{book.title}</span>
                                    )}
                                  </div>
                                )}
                                <div className={`absolute -top-2 -right-2 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold shadow ${
                                  book.isFound 
                                    ? 'bg-primary text-primary-foreground' 
                                    : 'bg-orange-500 text-white'
                                }`}>
                                  {book.position}
                                </div>
                              </div>
                              <p className="text-xs text-center line-clamp-1 w-[80px] mt-1">
                                {book.title}
                              </p>
                            </div>
                          ))}
                          {series.books.length > 7 && (
                            <div className="flex items-center text-muted-foreground text-sm">
                              +{series.books.length - 7} more
                            </div>
                          )}
                        </div>
                      )}

                      <Button
                        className="w-full"
                        onClick={() => handleSelectSeries(series)}
                      >
                        Select & Create Suggestion
                      </Button>
                    </div>
                  </Card>
                ))}
              </div>
            </ScrollArea>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
